"use client";

import { type ReactNode, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PlanePoint,
  type PlaneScales,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import {
  type Curve,
  curvePath,
  type Window,
} from "@/lib/tool-kit/calculus/plot";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

/**
 * A graph of one or more functions on the shared coordinate plane, with marked points,
 * a legend, and PNG / SVG download. Everything visible comes in through props.
 */

export interface GraphSeries {
  f: Curve;
  color: string;
  /** Legend text. */
  label: string;
  dashed?: boolean;
  width?: number;
}

export interface GraphPoint {
  x: number;
  y: number;
  color: string;
  label?: string;
  below?: boolean;
}

export function FunctionGraph({
  window,
  series,
  points = [],
  title,
  filename,
  downloadPngLabel,
  downloadSvgLabel,
  under,
  over,
}: {
  window: Window;
  series: GraphSeries[];
  points?: GraphPoint[];
  /** Accessible name of the graph: what it shows, in a sentence. */
  title: string;
  /** File name stem for the downloads. */
  filename: string;
  downloadPngLabel: string;
  downloadSvgLabel?: string;
  /** Drawn beneath the curves (shaded regions). */
  under?: (scales: PlaneScales) => ReactNode;
  /** Drawn above the curves (extra lines, markers). */
  over?: (scales: PlaneScales) => ReactNode;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  return (
    <div className="space-y-3">
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-foreground text-sm">
        {series.map((entry) => (
          <li className="flex items-center gap-2" key={entry.label}>
            <svg aria-hidden="true" height="10" width="24">
              <line
                stroke={entry.color}
                strokeDasharray={entry.dashed ? "5 3" : undefined}
                strokeWidth={entry.width ?? 2.5}
                x1="0"
                x2="24"
                y1="5"
                y2="5"
              />
            </svg>
            <span>{entry.label}</span>
          </li>
        ))}
      </ul>
      <CoordinatePlane
        formatTick={(value, decimals) =>
          Math.abs(value) >= 1e6
            ? fmt(value, { notation: "compact", maximumFractionDigits: 1 })
            : fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={window}
      >
        {(scales) => (
          <>
            {under ? under(scales) : null}
            {series.map((entry) => (
              <path
                d={curvePath(entry.f, window, scales.x, scales.y)}
                fill="none"
                key={entry.label}
                stroke={entry.color}
                strokeDasharray={entry.dashed ? "6 4" : undefined}
                strokeLinejoin="round"
                strokeWidth={entry.width ?? 2.5}
              />
            ))}
            {over ? over(scales) : null}
            {points.map((point) =>
              Number.isFinite(point.x) && Number.isFinite(point.y) ? (
                <PlanePoint
                  below={point.below}
                  color={point.color}
                  cx={scales.x(point.x)}
                  cy={scales.y(point.y)}
                  key={`${point.x}-${point.y}-${point.color}`}
                  label={point.label}
                />
              ) : null
            )}
          </>
        )}
      </CoordinatePlane>
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            if (ref.current) {
              downloadPng(`${filename}.png`, ref.current).catch(
                () => undefined
              );
            }
          }}
          type="button"
        >
          {downloadPngLabel}
        </button>
        {downloadSvgLabel ? (
          <button
            className={BUTTON_SMALL}
            onClick={() => {
              if (ref.current) {
                downloadSvg(`${filename}.svg`, ref.current);
              }
            }}
            type="button"
          >
            {downloadSvgLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
